import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Ticket,
  TicketMessage,
  TicketTag,
  CustomerProfile,
  AdminUser,
  STATUS_LABELS,
  PRIORITY_LABELS,
  DEPARTMENTS
} from './core/_models'
import {
  getTicketDetail,
  getTicketMessages,
  getAdminUsers,
  getTags,
  addTicketReply,
  updateTicket,
  assignTicket,
  switchToAccount
} from './core/_requests'
import { useAdminAuth } from '../auth/AdminAuthContext'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

const QUICK_FOLLOWUP_OPTIONS = [
  { label: 'עוד שעתיים', hours: 2 },
  { label: 'עוד 4 שעות', hours: 4 },
  { label: 'מחר', hours: 24 },
  { label: 'עוד יומיים', hours: 48 },
  { label: 'עוד שבוע', hours: 168 },
]

const TicketDetailPage: React.FC = () => {
  const params = useParams()
  const id = params.ticketId
  const navigate = useNavigate()
  const { admin: currentAdmin } = useAdminAuth()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null)

  // State
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [messages, setMessages] = useState<TicketMessage[]>([])
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null)
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [allTags, setAllTags] = useState<TicketTag[]>([])
  const [loading, setLoading] = useState(true)
  // Track message IDs that were unread when page first loaded
  const initialUnreadIds = useRef<Set<string>>(new Set())

  // Reply state
  const [replyText, setReplyText] = useState('')
  const [isInternalNote, setIsInternalNote] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [sending, setSending] = useState(false)

  // Follow-up state
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [followUpDate, setFollowUpDate] = useState('')
  const [followUpNote, setFollowUpNote] = useState('')

  // Tag picker
  const [showTagPicker, setShowTagPicker] = useState(false)
  const tagPickerRef = useRef<HTMLDivElement>(null)

  // Load data
  useEffect(() => {
    if (id) loadData()
  }, [id])

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!id) return
    const interval = setInterval(async () => {
      try {
        const res = await getTicketMessages(id)
        if (res.data.messages.length !== messages.length) {
          setMessages(res.data.messages)
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [id, messages.length])

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return

      if (e.key === 'r') {
        e.preventDefault()
        setIsInternalNote(false)
        replyTextareaRef.current?.focus()
      } else if (e.key === 'n') {
        e.preventDefault()
        setIsInternalNote(true)
        replyTextareaRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Close tag picker on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagPickerRef.current && !tagPickerRef.current.contains(e.target as Node)) {
        setShowTagPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadData = async () => {
    if (!id) return
    setLoading(true)
    try {
      const [detailRes, adminsRes, tagsRes] = await Promise.all([
        getTicketDetail(id),
        getAdminUsers(),
        getTags(),
      ])

      setTicket(detailRes.data.ticket)
      setMessages(detailRes.data.messages)
      setCustomerProfile(detailRes.data.customerProfile)
      setAdmins(adminsRes.data.admins)
      setAllTags(tagsRes.data.tags)

      // Capture which messages were unread for this admin on first load
      if (initialUnreadIds.current.size === 0 && currentAdmin) {
        detailRes.data.messages.forEach((msg: TicketMessage) => {
          const readByAdmins = (msg.read_by_admins || []).map((id: any) => id.toString ? id.toString() : id)
          if (!readByAdmins.includes(currentAdmin._id)) {
            initialUnreadIds.current.add(msg._id)
          }
        })
      }

      // Init follow-up fields
      if (detailRes.data.ticket.follow_up_date) {
        setFollowUpDate(moment(detailRes.data.ticket.follow_up_date).format('YYYY-MM-DDTHH:mm'))
        setFollowUpNote(detailRes.data.ticket.follow_up_note || '')
      }
    } catch (error) {
      console.error('Failed to load ticket:', error)
    } finally {
      setLoading(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendReply = async () => {
    if (!replyText.trim() || !id) return

    setSending(true)
    try {
      await addTicketReply(id, replyText, isInternalNote, attachments)
      setReplyText('')
      setAttachments([])
      setIsInternalNote(false)
      loadData()
    } catch (error) {
      console.error('Failed to send reply:', error)
    } finally {
      setSending(false)
    }
  }

  // Quick Resolve & Reply - sends reply and sets status to resolved
  const handleQuickResolve = async () => {
    if (!replyText.trim() || !id) return

    setSending(true)
    try {
      await addTicketReply(id, replyText, false, attachments)
      await updateTicket(id, { status: 'resolved' })
      setReplyText('')
      setAttachments([])
      setIsInternalNote(false)
      loadData()
    } catch (error) {
      console.error('Failed to quick resolve:', error)
    } finally {
      setSending(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return
    try {
      await updateTicket(id, { status: newStatus as any })
      loadData()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handlePriorityChange = async (newPriority: string) => {
    if (!id) return
    try {
      await updateTicket(id, { priority: newPriority as any })
      loadData()
    } catch (error) {
      console.error('Failed to update priority:', error)
    }
  }

  const handleDepartmentChange = async (dept: string | null) => {
    if (!id) return
    try {
      await updateTicket(id, { department: dept } as any)
      loadData()
    } catch (error) {
      console.error('Failed to update department:', error)
    }
  }

  const handleAssign = async (adminId: string | null) => {
    if (!id) return
    try {
      await assignTicket(id, adminId)
      loadData()
    } catch (error) {
      console.error('Failed to assign ticket:', error)
    }
  }

  const handleToggleTag = async (tagName: string) => {
    if (!id || !ticket) return
    const currentTags = [...ticket.tags]
    const newTags = currentTags.includes(tagName)
      ? currentTags.filter(t => t !== tagName)
      : [...currentTags, tagName]

    try {
      await updateTicket(id, { tags: newTags })
      loadData()
    } catch (error) {
      console.error('Failed to update tags:', error)
    }
  }

  const handleSaveFollowUp = async () => {
    if (!id) return
    try {
      await updateTicket(id, {
        follow_up_date: followUpDate || null,
        follow_up_note: followUpNote,
      })
      setShowFollowUp(false)
      loadData()
    } catch (error) {
      console.error('Failed to save follow-up:', error)
    }
  }

  const handleClearFollowUp = async () => {
    if (!id) return
    try {
      await updateTicket(id, { follow_up_date: null, follow_up_note: '' })
      setFollowUpDate('')
      setFollowUpNote('')
      setShowFollowUp(false)
      loadData()
    } catch (error) {
      console.error('Failed to clear follow-up:', error)
    }
  }

  const handleLoginAsAccount = async () => {
    if (!customerProfile?._id) return
    try {
      const res = await switchToAccount(customerProfile._id)
      const loginUrl = `${window.location.origin}/?overide-token=${res.data.token}`
      window.open(loginUrl, '_blank')
    } catch (err) {
      console.error('Failed to switch account:', err)
    }
  }

  const handleQuickFollowUp = async (hours: number) => {
    if (!id) return
    const date = moment().add(hours, 'hours').format('YYYY-MM-DDTHH:mm')
    try {
      await updateTicket(id, {
        follow_up_date: date,
        follow_up_note: followUpNote,
        status: 'follow_up' as any,
      })
      setFollowUpDate(date)
      setShowFollowUp(false)
      loadData()
    } catch (err) {
      console.error('Failed to set follow-up:', err)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(prev => [...prev, ...Array.from(e.target.files!)])
    }
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        handleSendReply()
      }
    }
  }

  const formatMessageTime = (date: string) => {
    const msgDate = moment(date)
    const today = moment().startOf('day')
    const yesterday = moment().subtract(1, 'day').startOf('day')

    if (msgDate.isSame(today, 'day')) {
      return `היום ב-${msgDate.format('HH:mm')}`
    } else if (msgDate.isSame(yesterday, 'day')) {
      return `אתמול ב-${msgDate.format('HH:mm')}`
    }
    return msgDate.format('DD/MM/YYYY HH:mm')
  }

  const getCustomerInitial = () => {
    if (customerProfile?.first_name) {
      return customerProfile.first_name.charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getTicketAge = () => {
    if (!ticket) return ''
    const duration = moment.duration(moment().diff(moment(ticket.created_at)))
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    if (days > 0) return `${days} ימים`
    return `${hours} שעות`
  }

  if (loading) {
    return (
      <div className="ticket-detail-page">
        <div className="chat-section">
          <div className="loading-state" style={{ height: '100%' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-page">
        <div className="chat-section">
          <div className="empty-state" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="empty-icon">
              <i className="bi bi-ticket-x"></i>
            </div>
            <div className="empty-title">הפנייה לא נמצאה</div>
            <div className="empty-text">הפנייה שחיפשת לא קיימת או נמחקה</div>
            <button
              className="btn-discord"
              style={{ marginTop: '20px', alignSelf: 'center' }}
              onClick={() => navigate('/admin/tickets')}
            >
              חזרה לרשימה
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ticket-detail-page">
      {/* Chat Section */}
      <div className="chat-section">
        {/* Header */}
        <div className="chat-header">
          <div className="header-right">
            <button className="back-btn" onClick={() => navigate('/admin/tickets')}>
              <i className="bi bi-arrow-right"></i>
              <span>חזרה</span>
            </button>
            <div className="ticket-info">
              <div className="ticket-title">
                <span className="ticket-hash">{ticket.ticket_number}</span>
                <span className="ticket-subject">{ticket.subject}</span>
                <span className="ticket-age">{getTicketAge()}</span>
              </div>
              <div className="ticket-subtitle">
                {ticket.is_internal && <span className="internal-ticket-badge">פנימי</span>}
                {ticket.department && (
                  <span className="department-badge">
                    <i className="bi bi-building"></i>
                    {DEPARTMENTS.find(d => d.value === ticket.department)?.label || ticket.department}
                  </span>
                )}
                נפתח {moment(ticket.created_at).format('DD/MM/YYYY HH:mm')} ·
                מקור: {ticket.source === 'web' ? 'אתר' : ticket.source === 'email' ? 'אימייל' : 'וידג\'ט'}
                {ticket.created_by && typeof ticket.created_by === 'object' && (
                  <> · נוצר ע"י {ticket.created_by.username}</>
                )}
              </div>
            </div>
          </div>

          <div className="header-left">
            <div className="header-field">
              <label className="header-field-label">סטטוס</label>
              <select
                className={`header-select status-${ticket.status}`}
                value={ticket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {Object.entries(STATUS_LABELS)
                  .filter(([k]) => !['all', 'active'].includes(k))
                  .map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
              </select>
            </div>

            <div className="header-field">
              <label className="header-field-label">עדיפות</label>
              <select
                className="header-select"
                value={ticket.priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="header-field">
              <label className="header-field-label">אחראי</label>
              <select
                className="header-select"
                value={ticket.assigned_to && typeof ticket.assigned_to === 'object' ? ticket.assigned_to._id : ''}
                onChange={(e) => handleAssign(e.target.value || null)}
              >
                <option value="">לא משויך</option>
                {admins.map(admin => (
                  <option key={admin._id} value={admin._id}>{admin.username}</option>
                ))}
              </select>
            </div>

            <div className="header-field">
              <label className="header-field-label">מחלקה</label>
              <select
                className="header-select"
                value={ticket.department || ''}
                onChange={(e) => handleDepartmentChange(e.target.value || null)}
              >
                <option value="">ללא מחלקה</option>
                {DEPARTMENTS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            {/* Follow-Up Button */}
            <div className="header-field">
              <label className="header-field-label">מעקב</label>
              <button
                className={`header-btn follow-up-btn ${ticket.follow_up_date ? 'has-followup' : ''}`}
                onClick={() => setShowFollowUp(!showFollowUp)}
                title="מעקב"
              >
                <i className="bi bi-bell"></i>
                {ticket.follow_up_date && (
                  <span className={`follow-up-indicator ${new Date(ticket.follow_up_date) <= new Date() ? 'overdue' : ''}`}></span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Follow-Up Panel */}
        {showFollowUp && (
          <div className="follow-up-panel">
            <div className="follow-up-content">
              <div className="follow-up-field">
                <label>תאריך מעקב</label>
                <input
                  type="datetime-local"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                />
              </div>
              <div className="follow-up-field">
                <label>הערת מעקב</label>
                <input
                  type="text"
                  placeholder="מה צריך לבדוק..."
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                />
              </div>
              <div className="follow-up-actions">
                <button className="btn-discord" onClick={handleSaveFollowUp}>שמור</button>
                {ticket.follow_up_date && (
                  <button className="btn-discord secondary" onClick={handleClearFollowUp}>הסר מעקב</button>
                )}
                <button className="btn-discord secondary" onClick={() => setShowFollowUp(false)}>ביטול</button>
              </div>
              <div className="quick-followup-row">
                <span className="quick-followup-label">מעקב מהיר:</span>
                {QUICK_FOLLOWUP_OPTIONS.map((opt) => (
                  <button
                    key={opt.hours}
                    className="quick-followup-btn"
                    onClick={() => handleQuickFollowUp(opt.hours)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tag Picker Bar */}
        <div className="tag-bar">
          <div className="current-tags">
            {ticket.tags.map((tagName, i) => {
              const tagObj = allTags.find(t => t.name === tagName)
              return (
                <span
                  key={i}
                  className="tag-chip removable"
                  style={tagObj ? { background: `${tagObj.color}20`, color: tagObj.color, borderColor: `${tagObj.color}40` } : {}}
                >
                  {tagName}
                  <button className="remove-tag" onClick={() => handleToggleTag(tagName)}>
                    <i className="bi bi-x"></i>
                  </button>
                </span>
              )
            })}
          </div>
          <div className="tag-picker-wrapper" ref={tagPickerRef}>
            <button className="add-tag-btn" onClick={() => setShowTagPicker(!showTagPicker)}>
              <i className="bi bi-tag"></i>
              הוסף תגית
            </button>
            {showTagPicker && (
              <div className="tag-picker-dropdown">
                {allTags
                  .filter(tag => !ticket.tags.includes(tag.name))
                  .map(tag => (
                    <button
                      key={tag._id}
                      className="tag-option"
                      onClick={() => {
                        handleToggleTag(tag.name)
                        setShowTagPicker(false)
                      }}
                    >
                      <span className="tag-color-dot" style={{ background: tag.color }}></span>
                      {tag.name}
                    </button>
                  ))}
                {allTags.filter(tag => !ticket.tags.includes(tag.name)).length === 0 && (
                  <div className="no-tags">כל התגיות כבר משויכות</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="messages-container">
          {messages.map((msg) => (
            <div
              key={msg._id}
              className={`message ${msg.sender_type}${initialUnreadIds.current.has(msg._id) ? ' unread-by-me' : ''}`}
            >
              {msg.sender_type === 'system' ? (
                <div className="system-message">
                  <i className="bi bi-info-circle system-icon"></i>
                  <span>{msg.message}</span>
                  <span className="system-time">{formatMessageTime(msg.created_at)}</span>
                </div>
              ) : (
                <>
                  <div className={`message-avatar ${msg.sender_type === 'customer' ? 'customer-avatar' : 'admin-avatar'}`}>
                    {msg.sender_type === 'customer'
                      ? getCustomerInitial()
                      : (msg.sender_admin && typeof msg.sender_admin === 'object'
                        ? msg.sender_admin.username.charAt(0).toUpperCase()
                        : 'A')}
                  </div>
                  <div className={`message-body ${msg.sender_type === 'customer' ? 'customer-message' : msg.is_internal_note ? 'internal-message' : 'admin-message'}`}>
                    <div className="message-header">
                      <span className="sender-name">
                        {msg.sender_type === 'customer'
                          ? `${customerProfile?.first_name || ''} ${customerProfile?.last_name || ''}`.trim() || 'לקוח'
                          : (msg.sender_admin && typeof msg.sender_admin === 'object'
                            ? msg.sender_admin.username
                            : 'Admin')}
                      </span>
                      <span className="message-time">{formatMessageTime(msg.created_at)}</span>
                      {msg.is_internal_note && (
                        <span className="internal-badge">הערה פנימית</span>
                      )}
                    </div>
                    <div className="message-content">{msg.message}</div>

                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="message-attachments">
                        {msg.attachments.map((att, i) => (
                          <a
                            key={i}
                            href={att.location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment"
                          >
                            <i className="bi bi-paperclip attachment-icon"></i>
                            <span className="attachment-name">{att.originalname}</span>
                            <span className="attachment-size">({Math.round(att.size / 1024)}KB)</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Composer */}
        <div className="composer">
          <div className="composer-options">
            <label className={`internal-toggle ${isInternalNote ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={isInternalNote}
                onChange={(e) => setIsInternalNote(e.target.checked)}
              />
              <span>הערה פנימית (לא תישלח ללקוח)</span>
            </label>
          </div>

          <div className="composer-input-wrapper">
            <textarea
              ref={replyTextareaRef}
              className={isInternalNote ? 'internal' : ''}
              placeholder={isInternalNote ? 'כתוב הערה פנימית...' : 'כתוב תגובה...'}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
            />
            <div className="composer-actions">
              <button
                className="action-btn attach-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="bi bi-paperclip"></i>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                className={`action-btn send-btn ${isInternalNote ? 'internal' : ''} ${sending ? 'sending' : ''}`}
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
                title="שלח (Ctrl+Enter)"
              >
                {sending ? (
                  <div className="spinner"></div>
                ) : (
                  <i className="bi bi-send"></i>
                )}
              </button>
            </div>
          </div>

          <div className="composer-footer">
            <div className="composer-footer-left">
              {attachments.length > 0 && (
                <div className="attachments-preview">
                  {attachments.map((file, i) => (
                    <div key={i} className="attachment-chip">
                      <i className="bi bi-paperclip"></i>
                      <span>{file.name}</span>
                      <button className="remove-btn" onClick={() => removeAttachment(i)}>
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Quick Resolve button */}
              {!isInternalNote && replyText.trim() && ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <button
                  className="quick-resolve-btn"
                  onClick={handleQuickResolve}
                  disabled={sending}
                >
                  <i className="bi bi-check-circle"></i>
                  שלח וסמן כנפתר
                </button>
              )}
            </div>
            <span className="shortcut-hint">
              <kbd>r</kbd> תגובה · <kbd>n</kbd> הערה · <kbd>Ctrl+Enter</kbd> שליחה
            </span>
          </div>
        </div>
      </div>

      {/* Customer Sidebar */}
      <div className="customer-sidebar">
        {!customerProfile && ticket.is_internal && (
          <div className="sidebar-section">
            <div className="section-title">פנייה פנימית</div>
            <div className="internal-ticket-info">
              <div className="internal-icon">
                <i className="bi bi-building"></i>
              </div>
              <p>פנייה פנימית ללא לקוח משויך</p>
              {ticket.department && (
                <div className="internal-dept">
                  <strong>מחלקה:</strong> {DEPARTMENTS.find(d => d.value === ticket.department)?.label || ticket.department}
                </div>
              )}
              {ticket.created_by && typeof ticket.created_by === 'object' && (
                <div className="internal-creator">
                  <strong>נוצר ע"י:</strong> {ticket.created_by.username}
                </div>
              )}
            </div>
          </div>
        )}
        {customerProfile && (
          <>
            {/* Customer Info */}
            <div className="sidebar-section">
              <div className="section-title">לקוח</div>
              <div className="customer-profile">
                <div className="profile-avatar">{getCustomerInitial()}</div>
                <div className="profile-info">
                  <div className="profile-name">
                    {customerProfile.first_name} {customerProfile.last_name}
                  </div>
                  <div className="profile-company">{customerProfile.company_name}</div>
                </div>
              </div>

              <button className="login-as-account-btn" onClick={handleLoginAsAccount}>
                <i className="bi bi-box-arrow-in-left"></i>
                כניסה כלקוח
              </button>

              {customerProfile.support_token && (
                <a
                  href={`/my-tickets/${customerProfile.support_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="customer-portal-link"
                >
                  <i className="bi bi-link-45deg"></i>
                  פורטל לקוח
                </a>
              )}

              <div className="info-item">
                <i className="bi bi-envelope info-icon"></i>
                <div className="info-content">
                  <div className="info-label">אימייל</div>
                  <div className="info-value">
                    <a href={`mailto:${customerProfile.email}`}>{customerProfile.email}</a>
                  </div>
                </div>
              </div>

              <div className="info-item">
                <i className="bi bi-phone info-icon"></i>
                <div className="info-content">
                  <div className="info-label">טלפון</div>
                  <div className="info-value">
                    <a href={`tel:${customerProfile.phone}`}>{customerProfile.phone || '-'}</a>
                  </div>
                </div>
              </div>

              <div className="info-item">
                <i className="bi bi-calendar info-icon"></i>
                <div className="info-content">
                  <div className="info-label">נרשם</div>
                  <div className="info-value">
                    {moment(customerProfile.created_at).format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription */}
            <div className="sidebar-section">
              <div className="section-title">מנוי</div>
              <div className="subscription-status">
                <div className="status-info">
                  <div className="status-plan">
                    {customerProfile.monthly_payment_amount
                      ? `₪${customerProfile.monthly_payment_amount}/חודש`
                      : 'לא מוגדר'}
                  </div>
                  <div className="status-price">
                    {customerProfile.trial_period_end
                      ? `עד ${moment(customerProfile.trial_period_end).format('DD/MM/YYYY')}`
                      : 'לא מוגדר'}
                  </div>
                </div>
                {customerProfile.trial_period_end && (
                  <span className={`status-badge ${new Date(customerProfile.trial_period_end) > new Date() ? 'active' : 'expired'}`}>
                    {new Date(customerProfile.trial_period_end) > new Date() ? 'פעיל' : 'פג תוקף'}
                  </span>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="sidebar-section">
              <div className="section-title">סטטיסטיקה</div>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{customerProfile.stats?.totalCampaigns || 0}</div>
                  <div className="stat-label">קמפיינים</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{customerProfile.stats?.totalBots || 0}</div>
                  <div className="stat-label">בוטים</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{customerProfile.stats?.totalProfiles || 0}</div>
                  <div className="stat-label">פרופילים</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{customerProfile.stats?.totalTickets || 0}</div>
                  <div className="stat-label">פניות</div>
                </div>
              </div>
            </div>

            {/* Previous Tickets */}
            {customerProfile.ticketHistory && customerProfile.ticketHistory.length > 0 && (
              <div className="sidebar-section">
                <div className="section-title">פניות קודמות</div>
                <div className="ticket-history-list">
                  {customerProfile.ticketHistory
                    .filter(t => t._id !== ticket?._id)
                    .slice(0, 5)
                    .map(t => (
                      <div
                        key={t._id}
                        className="history-item"
                        onClick={() => navigate(`/admin/tickets/${t._id}`)}
                      >
                        <div className="history-info">
                          <div className="history-number">{t.ticket_number}</div>
                          <div className="history-subject">{t.subject}</div>
                        </div>
                        <span className={`history-status ${t.status}`}>
                          {STATUS_LABELS[t.status]}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TicketDetailPage
